import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { IStore } from "../../redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import * as BluetoothActions from '../../redux/actions/bluetoothAction';
import { View, Platform, PermissionsAndroid } from 'react-native';
import { Box, Button, FormControl, IconButton, Input, VStack, Text, Select, Spinner, HStack } from 'native-base';
import { containerStyle } from 'src/styles/containerStyle';
import { inputStyle } from 'src/styles/inputStyle';
import CustomAlertDialog from '../CustomAlertDialog';
import CustomAlertBanner from '../CustomAlertBanner';
import MaterialIconByName from '../MaterialIconByName';
import WifiManager from 'react-native-wifi-reborn';
import { getText } from 'src/utils/i18n';

interface ConfigureWifiFormState {
  wifiSSID: string,
  wifiPassword: string
}

interface WifiNetwork {
  SSID?: string;
  BSSID?: string;
  capabilities?: string;
  frequency?: number;
  level?: number;
  timestamp?: number;
}

const isEnterpriseNetwork = (network: WifiNetwork): boolean => {
  if (!network.capabilities) return false;
  const caps = network.capabilities.toUpperCase();
  return caps.includes('EAP') || caps.includes('WPA2-EAP') || caps.includes('WPA-EAP');
};

const getNetworkDisplayName = (network: WifiNetwork): string => {
  const isEnterprise = isEnterpriseNetwork(network);
  return isEnterprise
    ? `${network.SSID} (Enterprise)`
    : network.SSID;
};

export default function ConfigureRpiWifi() {

  const isMountedRef = useRef(true);
  const [bannerVisible, setBannerVisible] = React.useState(false);
  const [securePasswordEntry, setSecurePasswordEntry] = React.useState(true);
  const [visible, setVisible] = React.useState(false);
  
  // WiFi scanning states
  const [wifiNetworks, setWifiNetworks] = React.useState<WifiNetwork[]>([]);
  const [isScanning, setIsScanning] = React.useState(false);
  const [scanError, setScanError] = React.useState<string>('');
  const [useManualEntry, setUseManualEntry] = React.useState(false);

  const [formData, setData] = React.useState<ConfigureWifiFormState>({
    wifiSSID: '',
    wifiPassword: ''
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const validate = () => {
    if (formData.wifiSSID === undefined
      || formData.wifiSSID.length === 0) {
      setErrors({
        wifiSSID: 'WiFi SSID is required'
      });
      return false;
    } else if (formData.wifiPassword === undefined
      || formData.wifiPassword.length === 0) {
      console.log('password is required')
      setErrors({
        wifiPassword: 'Password is required'
      });
      return false;
    }

    return true;
  };

  const showDialog = () => setVisible(true);

  const hideDialog = () => setVisible(false);

  const [buttonText, setButtonText] = useState(getText('configure'));
  const [buttonIcon, setButtonIcon] = useState("wifi");

  const wifiConfigured = useSelector((store: IStore) => store.bluetooth.configureWifi);

  const wifiStatus = useSelector((store: IStore) => store.bluetooth.rpiStatus.status);

  const dispatch = useDispatch();

  // WiFi scanning functions
  const requestLocationPermission = async () => {
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
          {
            title: getText('locationPermissionTitle'),
            message: getText('locationPermissionMessage'),
            buttonNeutral: getText('askMeLater'),
            buttonNegative: 'Cancel',
            buttonPositive: 'OK',
          }
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      } catch (err) {
        console.warn('Location permission error:', err);
        return false;
      }
    }
    return true;
  };

  const scanWifiNetworks = useCallback(async () => {
    if (Platform.OS === 'android') {
      setIsScanning(true);
      setScanError('');
      
      try {
        const hasPermission = await requestLocationPermission();
        if (!hasPermission) {
          if (isMountedRef.current) setScanError(getText('locationPermissionRequired'));
          return;
        }

        const networks = await WifiManager.reScanAndLoadWifiList();

        // First deduplicate - remove hidden networks and duplicates
        const uniqueNetworks = networks.filter((network, index, self) =>
          network.SSID?.length > 0 &&
          self.findIndex(n => n.SSID === network.SSID) === index
        );

        if (isMountedRef.current) setWifiNetworks(uniqueNetworks);
      } catch (error) {
        console.error('WiFi scan error:', error);
        if (isMountedRef.current) setScanError(getText('wifiScanFailed'));
      } finally {
        if (isMountedRef.current) setIsScanning(false);
      }
    }
  }, []);

  const getCurrentWifiSSID = useCallback(async () => {
    if (Platform.OS === 'ios') {
      try {
        const ssid = await WifiManager.getCurrentWifiSSID();
        if (ssid && ssid !== '<unknown ssid>') {
          if (isMountedRef.current) setData(prev => ({ ...prev, wifiSSID: ssid }));
        }
      } catch (error) {
        console.log('Could not get current WiFi SSID:', error);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    dispatch(BluetoothActions.getRpiStatus.request());
    
    // Initialize WiFi functionality based on platform
    (async () => {
      if (Platform.OS === 'android') {
        await scanWifiNetworks();
      } else if (Platform.OS === 'ios') {
        await getCurrentWifiSSID();
      }
    })();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [dispatch, scanWifiNetworks, getCurrentWifiSSID]);

  // Add auto-refresh for status every 10 seconds to detect connection changes
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(BluetoothActions.getRpiStatus.request());
    }, 10000);
    
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (wifiStatus?.wifiStatus === "CONNECTED") {
      setBannerVisible(true);
      // If we have config from status, update the form data to reflect connected network
      if (wifiStatus.config?.wifiSSID && wifiStatus.config.wifiSSID !== formData.wifiSSID) {
        setData(prev => ({ 
          ...prev, 
          wifiSSID: wifiStatus.config!.wifiSSID,
          // Don't overwrite password for security
        }));
      }
    } else if (wifiStatus?.wifiStatus === "DISCONNECTED" || wifiStatus?.wifiStatus === "NOT_CONNECTED") {
      setBannerVisible(false);
    }
  }, [wifiStatus]);

  const updateStatus = useCallback(() => {
    console.log("ConfigureRpiWifi updateStatus", wifiConfigured);
    if (wifiConfigured.configured) {
      setButtonText(getText('configured'));
      setButtonIcon("check");
    } else if (wifiConfigured.isFetching) {
      setButtonText(getText('configuring'));
      setButtonIcon("sync");
    } else {
      setButtonText(getText('configure'));
      setButtonIcon("wifi");
    }
  }, [wifiConfigured]);

  useEffect(() => {
    updateStatus();
  }, [updateStatus]);

  const getWifiStatusDisplay = () => {
    if (!wifiStatus) return { text: getText('checking'), color: 'gray.500' };
    
    switch (wifiStatus.wifiStatus) {
      case 'CONNECTED':
        return { text: `${getText('connectedTo')}${wifiStatus.config?.wifiSSID ? ` ${wifiStatus.config.wifiSSID}` : ''}`, color: 'green.500' };
      case 'DISCONNECTED':
        return { text: getText('disconnected'), color: 'red.500' };
      case 'CONNECTING':
        return { text: getText('connecting'), color: 'orange.500' };
      default:
        return { text: getText('notConnected'), color: 'gray.500' };
    }
  };

  // Memoize network list with enterprise flags to avoid redundant calculations
  const networkListItems = useMemo(() => {
    return wifiNetworks.map((network) => ({
      network,
      displayName: getNetworkDisplayName(network),
      isEnterprise: isEnterpriseNetwork(network)
    }));
  }, [wifiNetworks]);

  // Check if selected network is enterprise
  const selectedNetworkIsEnterprise = useMemo(() => {
    return formData.wifiSSID && networkListItems.find(
      item => item.network.SSID === formData.wifiSSID && item.isEnterprise
    );
  }, [formData.wifiSSID, networkListItems]);

  const onSubmit = () => {
    const isValid = validate();
    if (isValid) {
      // submit the data
      showDialog()
    }
  };

  return (
    <View style={containerStyle.mainContainer}>
      <CustomAlertBanner
        title={`The IoT device is already connected to Internet${wifiStatus?.config?.wifiSSID ? ` via "${wifiStatus.config.wifiSSID}"` : ''}. Modify the settings to update Wifi credentials.`}
        status='success'
        isVisible={bannerVisible}
      />
      <CustomAlertDialog
        title='Configure WiFi'
        description="This will update the WiFi config to the IoT device and connect to the new WiFi. Click confirm to continue with the changes."
        cancelText='Cancel'
        confirmText='Confirm'
        isOpen={visible}
        onCancelClicked={hideDialog}
        onConfirmClicked={() => {
          dispatch(BluetoothActions.configueRpiWifi.request({
            wifiSSID: formData.wifiSSID,
            wifiPassword: formData.wifiPassword
          }));
          hideDialog();
        }}
      />
      <Box>
        {/* WiFi Status Indicator */}
        <HStack alignItems="center" space={2} mb={4} p={3} bg="gray.100" borderRadius="md">
          <MaterialIconByName name="wifi" size="sm" />
          <VStack flex={1}>
            <Text fontSize="sm" fontWeight="medium">{getText('deviceWifiStatus')}</Text>
            <Text fontSize="sm" color={getWifiStatusDisplay().color}>
              {getWifiStatusDisplay().text}
            </Text>
          </VStack>
          <IconButton
            size="sm"
            variant="ghost"
            onPress={() => dispatch(BluetoothActions.getRpiStatus.request())}
            icon={<MaterialIconByName name="refresh" />}
            accessibilityLabel="Refresh WiFi status"
          />
        </HStack>
        
        <VStack style={{
          marginTop: 18
        }}>
          <FormControl isRequired>
            <FormControl.Label>WiFi SSID</FormControl.Label>
            
            {/* WiFi Network Selection - Android with toggle, iOS manual only */}
            {Platform.OS === 'android' ? (
              <VStack space={2}>
                <HStack space={1} mb={2}>
                  <Button
                    flex={1}
                    size="sm"
                    variant={!useManualEntry ? "solid" : "outline"}
                    onPress={() => setUseManualEntry(false)}>
                    Scan
                  </Button>
                  <Button
                    flex={1}
                    size="sm"
                    variant={useManualEntry ? "solid" : "outline"}
                    onPress={() => setUseManualEntry(true)}>
                    Manual
                  </Button>
                </HStack>

                {!useManualEntry ? (
                  <>
                    <HStack alignItems="center" space={2}>
                      <Select
                        flex={1}
                        selectedValue={formData.wifiSSID}
                        accessibilityLabel="Choose WiFi Network"
                        placeholder="Select WiFi Network"
                        style={inputStyle.nativeInput}
                        isInvalid={'wifiSSID' in errors}
                        onValueChange={(value) => setData({ ...formData, wifiSSID: value })}>
                        {networkListItems.map((item) => (
                          <Select.Item
                            key={item.network.BSSID}
                            label={item.displayName}
                            value={item.network.SSID}
                          />
                        ))}
                      </Select>
                      <IconButton
                        onPress={scanWifiNetworks}
                        disabled={isScanning}
                        icon={isScanning ? <Spinner size="sm" /> : <MaterialIconByName name="refresh" />}
                        accessibilityLabel="Rescan WiFi networks"
                      />
                    </HStack>
                    {scanError ? <Text color="red.500" fontSize="sm">{scanError}</Text> : null}
                    {selectedNetworkIsEnterprise && (
                      <Text color="orange.500" fontSize="sm">
                        {getText('enterpriseNetworkWarning')}
                      </Text>
                    )}
                  </>
                ) : (
                  <Input
                    style={inputStyle.nativeInput}
                    isInvalid={'wifiSSID' in errors}
                    placeholder="WiFi SSID"
                    value={formData.wifiSSID}
                    onChangeText={value => setData({ ...formData, wifiSSID: value })}
                  />
                )}
              </VStack>
            ) : (
              /* iOS and other platforms: Manual SSID input with autofill */
              <VStack space={2}>
                <HStack alignItems="center" space={2}>
                  <Input
                    flex={1}
                    style={inputStyle.nativeInput}
                    isInvalid={'wifiSSID' in errors}
                    placeholder="WiFi SSID"
                    value={formData.wifiSSID}
                    onChangeText={value => setData({ ...formData, wifiSSID: value })}
                  />
                  <IconButton
                    onPress={getCurrentWifiSSID}
                    icon={<MaterialIconByName name="my-location" />}
                    accessibilityLabel="Auto-fill current WiFi network"
                  />
                </HStack>
                <Text color="gray.500" fontSize="xs">
                  {getText('autofillWifiHint')}
                </Text>
              </VStack>
            )}
            
            {'wifiSSID' in errors ? <FormControl.ErrorMessage>{errors['wifiSSID']}</FormControl.ErrorMessage> : null}

            <FormControl.Label>{getText('wifiPasswordLabel')}</FormControl.Label>
            <Input
              secureTextEntry={securePasswordEntry}
              style={inputStyle.nativeInput}
              isInvalid={'wifiPassword' in errors}
              InputRightElement={
                <IconButton
                  onPress={() => {
                    setSecurePasswordEntry(!securePasswordEntry)
                  }}
                  icon={<MaterialIconByName name={"eye"} />} />
              }
              placeholder="********"
              onSubmitEditing={onSubmit}
              onChangeText={value => setData({
                ...formData,
                wifiPassword: value
              })} />
            {'wifiPassword' in errors ? <FormControl.ErrorMessage>{errors['wifiPassword']}</FormControl.ErrorMessage> : null}
          </FormControl>
          <Button
            leftIcon={<MaterialIconByName name={buttonIcon} />}
            onPress={onSubmit} mt="5">
            {buttonText}
          </Button>
          {'formError' in errors ? <Text color="red.500" mt="2">{(errors as any)['formError'] || ""}</Text> : null}
        </VStack>
      </Box>
    </View>
  );
};
